import { gql } from '@apollo/client/core';
import { client } from '@/shared/apollo-api/business/client';
import type { ApolloQueryResult } from '@apollo/client/core';
import type { ProductPageQuery } from '@/shared/apollo-api/__generated__/types';

export const getProductPage = async (
  id: number,
  cityId: number
): Promise<ApolloQueryResult<ProductPageQuery>> => {
  const response = await client.query({
    query: gql`
      query ProductPage(
        $productId: Int!
        $cityId: Int!
        $linkTrans4: Transformation!
        $linkTrans6: Transformation!
      ) {
        product(id: $productId) {
          ordersQuantity
          feedbackQuantity
          externalRating
          comments {
            value
            type
          }
          photo360 {
            key
            link(trans: PRODUCT_720) {
              high
              low
            }
          }
          photos {
            key
            thumbnail: link(trans: PRODUCT_240) {
              low
            }
            link(trans: PRODUCT_720) {
              high
              low
            }
            original {
              high
            }
          }
          rating
          video {
            key
            url
          }
          title
          id
          category {
            id
            parentList {
              id
              title
            }
            title
          }
          minFullPrice
          minSellPrice
          characteristics {
            id
            title
            type
            values {
              id
              photo {
                key
                link(trans: PRODUCT_240) {
                  high
                  low
                }
              }
              title
              value
            }
          }
          badges {
            ... on BottomIconTextBadge {
              backgroundColor
              description
              iconLink
              id
              link
              text
              textColor
            }
            ... on BottomTextBadge {
              backgroundColor
              description
              id
              link
              text
              textColor
            }
            ... on TopTextBadge {
              backgroundColor
              id
              text
              textColor
            }
          }
          description
          favorite
          shop {
            avatar {
              low
            }
            feedbackQuantity
            id
            official
            ordersQuantity
            rating
            seller {
              accountId
              legalRecords {
                name
                value
              }
            }
            shortTitle
            title
            url
          }
          shortDescription
          skuList {
            cardNearestDeliveryInfo(cityId: $cityId) {
              nearestDelivery
            }
            id
            skuGroup {
              id
            }
            availableAmount
            photo {
              key
              link(trans: $linkTrans4) {
                low
              }
            }
            video {
              key
              url
            }
            paymentOptions {
              paymentInfo
              text
              type
              id
              active
              titleBanner
            }
            skuTitle
            sellPrice
            properties {
              description
              filter {
                description
                id
                measurementUnit
                title
                type
              }
              id
              image
              name
            }
            discountBadge {
              backgroundColor
              id
              text
              textColor
            }
            characteristicValues {
              id
              photo {
                key
                link(trans: $linkTrans6) {
                  low
                }
              }
              title
              value
              characteristic {
                id
                title
                type
              }
            }
            fullPrice
            vat {
              vatRate
              vatAmount
              type
              price
            }
            discountTimer {
              endDate
              image {
                high
                low
              }
              text
              textColor
            }
          }
          attributes
          cardNearestDeliveryInfo(cityId: $cityId) {
            nearestDelivery
          }
        }
        productPage(id: $productId) {
          reviewsPhotoAmount
          actions {
            location
            type
            ... on MotivationAction {
              image {
                low
                high
              }
              location
              text
              type
            }
            ... on WishSaleAction {
              dateEnd
              location
              pressed
              pressedCount
              type
            }
          }
        }
      }
    `,
    variables: {
      productId: id,
      cityId,
      trans: 'PRODUCT_240',
      linkTrans4: 'PRODUCT_240',
      linkTrans5: 'PRODUCT_240',
      linkTrans6: 'PRODUCT_240',
      linkTrans7: 'PRODUCT_240',
    },
  });

  return response;
};

// Легкий запрос для получения shopId и photos
export const getProductLite = async (
  id: number
): Promise<{
  productLite: Pick<ProductPageQuery['product'], 'shop' | 'photos' | 'rating'> | null;
  errors: ReadonlyArray<any>;
}> => {
  const {
    data: { product },
    errors,
  } = await client.query({
    query: gql`
      query getProductLite($productId: Int!) {
        product(id: $productId) {
          shop {
            id
          }
          photos {
            link(trans: PRODUCT_240) {
              high
              low
            }
          }
          rating
        }
      }
    `,
    variables: {
      productId: id,
    },
    errorPolicy: 'all',
  });

  return {
    productLite: product,
    errors: errors || [],
  };
};
